import { VideoQuality } from 'livekit-client'

/**
 * Допуск сервера при выборе слоя: слой может быть на 10 процентов меньше запроса.
 *
 * LiveKit умножает высоту плитки на этот коэффициент, прежде чем сравнить её
 * со слоями отправителя. Мы повторяем ту же арифметику, иначе на телефоне
 * выбрали бы другую ступень, чем сам сервер.
 */
export const LAYER_SELECTION_TOLERANCE = 0.9

/**
 * Выбирает слой по высоте плитки так же, как это делает сервер LiveKit.
 *
 * Слой — одна из готовых версий картинки, которые отправитель уже кодирует.
 * Индекс в отсортированном списке высот совпадает со ступенью: первый слой
 * это LOW, второй MEDIUM, третий и дальше HIGH. Если слоёв ещё нет, считаем
 * выбор верхним: так безопаснее для батареи после шага вниз.
 *
 * @param layerHeights - высоты слоёв отправителя, например 216, 540 и 720
 * @param requestedHeight - высота, которую запросил adaptiveStream
 */
export function selectQualityForHeight(
  layerHeights: number[],
  requestedHeight: number,
): VideoQuality {
  const sortedHeights = [...layerHeights]
    .filter((height) => height > 0)
    .sort((left, right) => left - right)

  if (sortedHeights.length === 0) {
    return VideoQuality.HIGH
  }

  const requestedSize = requestedHeight * LAYER_SELECTION_TOLERANCE
  let qualityIndex = 0

  for (let index = 0; index < sortedHeights.length; index += 1) {
    qualityIndex = index

    if (index === sortedHeights.length - 1) {
      break
    }

    const currentHeight = sortedHeights[index]
    const nextHeight = sortedHeights[index + 1]

    if (currentHeight >= requestedSize && currentHeight !== nextHeight) {
      break
    }
  }

  if (qualityIndex <= 0) {
    return VideoQuality.LOW
  }

  if (qualityIndex === 1) {
    return VideoQuality.MEDIUM
  }

  return VideoQuality.HIGH
}

/**
 * Опускает выбранный слой на одну ступень, не ниже самого мелкого.
 *
 * На самом нижнем слое видео не выключаем: ниже опускать уже некуда.
 */
export function downgradeVideoQuality(quality: VideoQuality): VideoQuality {
  if (quality === VideoQuality.HIGH) {
    return VideoQuality.MEDIUM
  }

  if (quality === VideoQuality.MEDIUM) {
    return VideoQuality.LOW
  }

  return VideoQuality.LOW
}

/**
 * Считает ступень «на одну ниже», исходя из высоты плитки и слоёв отправителя.
 *
 * Именно от размера плитки, а не от уже подписанного слоя: иначе после первого
 * снижения хук увидел бы средний слой и опустил бы ещё раз.
 *
 * @param layerHeights
 * @param requestedHeight
 */
export function downgradedQualityForAdaptiveHeight(
  layerHeights: number[],
  requestedHeight: number,
): VideoQuality {
  return downgradeVideoQuality(selectQualityForHeight(layerHeights, requestedHeight))
}

/**
 * Собирает положительные высоты слоёв из описания трека.
 */
export function layerHeightsFromTrackLayers(
  layers: Array<{ height?: number }> | undefined,
): number[] {
  if (!layers) {
    return []
  }

  return layers
    .map((layer) => layer.height ?? 0)
    .filter((height) => height > 0)
}

/**
 * Возвращает ступень для телефона, включая случай, когда плитку ещё не измерили.
 *
 * Пока высоты нет, берём средний слой: так мы не оставляем верхний, но и не
 * гадаем, что плитка уже мелкая.
 *
 * @param layerHeights
 * @param requestedHeight - высота от adaptiveStream; пусто, если элемент ещё не прикреплён
 */
export function resolveDowngradedCameraQuality(
  layerHeights: number[],
  requestedHeight: number | undefined,
): VideoQuality {
  if (requestedHeight === undefined || requestedHeight <= 0) {
    return VideoQuality.MEDIUM
  }

  return downgradedQualityForAdaptiveHeight(layerHeights, requestedHeight)
}

/**
 * Слой отправителя: ступень и размер готовой картинки.
 *
 * `quality` здесь число, а не VideoQuality клиента: у протокола в том же поле
 * есть ещё OFF, и TypeScript считает эти два перечисления разными типами.
 */
type QualityLayer = {
  quality?: number
  width?: number
  height?: number
}

/**
 * Считает размер запроса, который сервер прочитает как нужную ступень.
 *
 * LiveKit при включённом adaptiveStream подменяет quality размером плитки,
 * если площадь плитки меньше слоя. Сервер тогда выбирает слой по высоте
 * плитки — без шага вниз. Берём меньшую сторону плитки и слоя: площадь
 * не больше плитки, поэтому подмены нет, а высота совпадает со ступенью.
 *
 * @param layers
 * @param quality - уже опущенная ступень
 * @param tile - размер плитки с учётом плотности экрана
 */
export function dimensionsForDowngradedRequest(
  layers: QualityLayer[] | undefined,
  quality: VideoQuality,
  tile: { width: number; height: number } | undefined,
): { width: number; height: number } | undefined {
  if (!tile || tile.width <= 0 || tile.height <= 0) {
    return undefined
  }

  const layer = layers?.find((item) => item.quality === quality)
  const layerHeight = layer?.height ?? 0
  const layerWidth = layer?.width ?? 0

  if (layerHeight <= 0) {
    return undefined
  }

  return {
    width: layerWidth > 0 ? Math.min(tile.width, layerWidth) : tile.width,
    height: Math.min(tile.height, layerHeight),
  }
}
